import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { SupportContactInline } from "@/components/SupportContact";
import {
  Sparkle, ArrowRight, DownloadSimple, ArrowClockwise, Lightning, CheckCircle, Rocket, ShareNetwork,
} from "@phosphor-icons/react";
import { generateShareCard, shareOrDownloadCard } from "@/lib/shareCard";

const STYLES = [
  "Modern minimal", "Bold & playful", "Elegant serif", "Handwritten", "Geometric",
  "Vintage / retro", "Luxury / premium", "Tech / futuristic",
];
const COLOR_MOODS = [
  "Warm earthy tones", "Vibrant & energetic", "Monochrome black & white",
  "Cool blues & greens", "Pastel & soft", "Deep jewel tones", "Sunset gradient",
];

const setSEO = (title, desc) => {
  document.title = title;
  const q = (sel) => document.querySelector(sel);
  q('meta[name="description"]')?.setAttribute("content", desc);
  q('meta[property="og:title"]')?.setAttribute("content", title);
  q('meta[property="og:description"]')?.setAttribute("content", desc);
};

const downloadPng = (base64, filename) => {
  const link = document.createElement("a");
  link.href = `data:image/png;base64,${base64}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function LogoStudio() {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    name: "", industry: "", styles: [], color_mood: "Warm earthy tones", symbol_hint: "",
  });
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [sharing, setSharing] = useState(null); // id of the logo whose card is being generated

  useEffect(() => {
    setSEO(
      "Free AI Logo Generator — Talaio/AI",
      "Generate a professional logo for your Indian business in 60 seconds. Free AI logo generator by Talaio/AI. Download PNG instantly. No signup required."
    );
  }, []);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleStyle = (s) => setF({
    ...f, styles: f.styles.includes(s)
      ? f.styles.filter((x) => x !== s)
      : (f.styles.length >= 3 ? f.styles : [...f.styles, s]),
  });

  const generate = async () => {
    if (!f.name.trim() || !f.industry.trim()) {
      toast.error("We need a name and an industry to generate.");
      setStep(1); return;
    }
    setLoading(true);
    setLogos([]);
    setSelectedId(null);
    try {
      const { data } = await api.post("/logo/generate", {
        name: f.name.trim(),
        industry: f.industry.trim(),
        styles: f.styles,
        color_mood: f.color_mood,
        symbol_hint: f.symbol_hint.trim() || null,
      });
      setLogos(data.logos);
      setRemaining(data.remaining_today);
      setStep(5);
      toast.success(`4 logo concepts ready. ${data.remaining_today} free generations left today.`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Generation failed. Please try again.";
      toast.error(msg);
      if (err.response?.status === 429) setRemaining(0);
    } finally { setLoading(false); }
  };

  const regenerate = () => generate();

  const shareLogo = async (logo, index) => {
    setSharing(logo.id);
    try {
      const dataUrl = await generateShareCard({
        logoBase64: logo.image_base64,
        brandName: f.name,
      });
      const filename = `${f.name.replace(/\s+/g, "-").toLowerCase()}-share-card-${index + 1}.png`;
      const shareText = `Meet ${f.name}. Our brand-new logo — made in 60 seconds with Talaio/AI's free AI logo generator. talaio.com/logo-studio`;
      const outcome = await shareOrDownloadCard(dataUrl, filename, shareText);
      toast.success(outcome === "shared" ? "Shared!" : "Share card downloaded — post it on Instagram / X / LinkedIn 🎉");
    } catch (err) {
      toast.error("Couldn't create the share card. Please try again.");
    } finally { setSharing(null); }
  };

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      {/* Hero */}
      <section className="border-b border-[var(--pf-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-14 md:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="pf-label mb-4 inline-flex items-center gap-2 text-[var(--pf-brand)]">
              <Sparkle size={14} weight="fill"/> Free AI Logo Generator
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
              A logo in 60 seconds.
              <br/><span className="text-[var(--pf-brand)]">Free. No signup.</span>
            </h1>
            <p className="mt-5 text-lg text-[var(--pf-text-2)] max-w-2xl">
              Tell us your brand name and industry — we&apos;ll generate 4 professional logo
              concepts you can download instantly. Perfect for founders, cafés, D2C brands and
              new businesses launching in India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Studio */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-14">
        {/* Progress dots */}
        <div className="flex items-center gap-3 mb-10">
          {[1,2,3,4].map((n) => (
            <div key={n} className="flex-1 h-1.5 rounded-full transition-colors"
                 style={{ background: step >= n ? "var(--pf-brand)" : "rgba(0,0,0,0.08)" }}/>
          ))}
        </div>

        {/* Step 1 — name */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <div className="pf-label">Step 1 of 4</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter">What&apos;s your brand called?</h2>
            <input required data-testid="logo-name" placeholder="e.g. Green Flags, Brew Lab, Kiran Studio"
              className="pf-input text-lg" value={f.name} onChange={upd("name")} autoFocus />
            <div className="flex justify-end">
              <button type="button" onClick={() => f.name.trim() && setStep(2)} disabled={!f.name.trim()}
                data-testid="logo-next-1" className="pf-btn-primary disabled:opacity-40">
                Next → industry
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 2 — industry */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <div className="pf-label">Step 2 of 4</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter">What business are you in?</h2>
            <input required data-testid="logo-industry" placeholder="e.g. coffee shop, D2C skincare, tuition centre, salon"
              className="pf-input text-lg" value={f.industry} onChange={upd("industry")} autoFocus />
            <div className="flex justify-between">
              <button type="button" onClick={() => setStep(1)} className="pf-btn-outline">← Back</button>
              <button type="button" onClick={() => f.industry.trim() && setStep(3)} disabled={!f.industry.trim()}
                data-testid="logo-next-2" className="pf-btn-primary disabled:opacity-40">
                Next → style
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3 — styles */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <div className="pf-label">Step 3 of 4</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter">Pick up to 3 style vibes.</h2>
            <p className="text-sm text-[var(--pf-text-2)]">Optional but helps the AI a lot.</p>
            <div className="flex flex-wrap gap-2">
              {STYLES.map((s) => (
                <button key={s} type="button" onClick={() => toggleStyle(s)}
                  data-testid={`logo-style-${s.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  className={`text-xs uppercase tracking-widest font-bold px-3 py-2 border-2 transition-colors ${
                    f.styles.includes(s)
                      ? "bg-[var(--pf-text)] text-white border-[var(--pf-text)]"
                      : "bg-white border-[var(--pf-border)] hover:border-[var(--pf-text)]"
                  }`}>{s}</button>
              ))}
            </div>
            <div className="flex justify-between">
              <button type="button" onClick={() => setStep(2)} className="pf-btn-outline">← Back</button>
              <button type="button" onClick={() => setStep(4)} data-testid="logo-next-3" className="pf-btn-primary">
                Next → colours
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 4 — colour mood + optional symbol hint */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <div className="pf-label">Step 4 of 4</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter">Pick a colour mood.</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {COLOR_MOODS.map((c) => (
                <button key={c} type="button" onClick={() => setF({ ...f, color_mood: c })}
                  data-testid={`logo-color-${c.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  className={`text-left px-4 py-3 border-2 transition-colors font-semibold text-sm ${
                    f.color_mood === c ? "border-[var(--pf-brand)] bg-[var(--pf-brand)]/5" : "border-[var(--pf-border)] hover:border-[var(--pf-text)]"
                  }`}>{c}</button>
              ))}
            </div>
            <div>
              <label className="pf-label block mb-2">Any symbol / motif you want in the logo? (optional)</label>
              <input data-testid="logo-symbol" placeholder="e.g. coffee bean, leaf, mountain, wave — leave blank if unsure"
                className="pf-input" value={f.symbol_hint} onChange={upd("symbol_hint")} />
            </div>
            <div className="flex justify-between items-center">
              <button type="button" onClick={() => setStep(3)} className="pf-btn-outline">← Back</button>
              <button type="button" onClick={generate} disabled={loading}
                data-testid="logo-generate" className="pf-btn-primary inline-flex items-center gap-2 text-base">
                {loading
                  ? (<><ArrowClockwise size={16} className="animate-spin"/> Generating 4 logos…</>)
                  : (<><Sparkle size={16} weight="fill"/> Generate 4 free logos</>)}
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 5 — results */}
        {step === 5 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="pf-label">Your logo concepts</div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter">4 concepts for {f.name}.</h2>
                {remaining !== null && (
                  <p className="text-xs text-[var(--pf-text-3)] mt-2 inline-flex items-center gap-1">
                    <Lightning size={14} weight="fill" className="text-[var(--pf-brand)]"/> {remaining} free generation{remaining === 1 ? "" : "s"} left today
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={regenerate} disabled={loading || remaining === 0}
                  data-testid="logo-regenerate" className="pf-btn-outline inline-flex items-center gap-2 disabled:opacity-40">
                  <ArrowClockwise size={14} className={loading ? "animate-spin" : ""}/> Regenerate
                </button>
                <button onClick={() => { setStep(1); setLogos([]); setSelectedId(null); }} className="pf-btn-outline">
                  Start over
                </button>
              </div>
            </div>

            {logos.length === 0 ? (
              <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]">
                Loading…
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {logos.map((l, i) => (
                  <div key={l.id}
                    onClick={() => setSelectedId(l.id)}
                    data-testid={`logo-result-${i}`}
                    className={`group border-2 bg-white cursor-pointer transition-all rounded-xl overflow-hidden ${
                      selectedId === l.id ? "border-[var(--pf-brand)] shadow-lg scale-[1.01]" : "border-[var(--pf-border)] hover:border-[var(--pf-text)]"
                    }`}>
                    <div className="aspect-square bg-white flex items-center justify-center overflow-hidden p-4">
                      <img src={`data:image/png;base64,${l.image_base64}`} alt={`Logo concept ${i+1}`} className="w-full h-full object-contain" />
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--pf-border)] bg-[var(--pf-bg)]">
                      <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)]">
                        Concept {i + 1}
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={(e) => { e.stopPropagation(); shareLogo(l, i); }}
                          disabled={sharing === l.id}
                          data-testid={`logo-share-${i}`}
                          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)] hover:text-[var(--pf-brand)] disabled:opacity-40">
                          {sharing === l.id
                            ? <ArrowClockwise size={13} className="animate-spin"/>
                            : <ShareNetwork size={13}/>}
                          Share
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); downloadPng(l.image_base64, `${f.name.replace(/\s+/g, "-").toLowerCase()}-logo-${i+1}.png`); }}
                          data-testid={`logo-download-${i}`}
                          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] hover:underline">
                          <DownloadSimple size={13}/> PNG
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Upsell */}
            <div className="mt-8 border border-[var(--pf-border)] bg-[var(--pf-text)] text-white p-6 md:p-8 rounded-2xl relative overflow-hidden"
              style={{ background: "linear-gradient(155deg, #1F1917 0%, #241C19 55%, #2A211D 100%)" }}>
              <div className="pf-cmyk-bar"><span/><span/><span/><span/></div>
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[var(--pf-brand)]/15 text-[var(--pf-brand)] text-[10px] font-bold rounded-full uppercase tracking-wider">
                    <Rocket size={12} weight="fill"/> Next step
                  </div>
                  <h3 className="mt-3 text-2xl md:text-3xl font-black tracking-tighter">Turn this logo into a launched brand.</h3>
                  <p className="mt-3 text-sm text-white/70">
                    Menu cards, business cards, signage, packaging, a 15-sec brand reel — all using
                    your new logo. Delivered in 7 days from ₹9,999.
                  </p>
                </div>
                <div className="flex flex-col gap-3 md:items-end">
                  <Link to="/startup-kit" data-testid="logo-cta-startup-kit"
                    className="inline-flex items-center justify-center gap-2 bg-[var(--pf-brand)] hover:bg-white hover:text-[#1F1917] text-white px-6 py-3 font-bold rounded-lg transition-colors">
                    Launch my brand <ArrowRight size={14}/>
                  </Link>
                  <ul className="text-xs text-white/60 space-y-1 md:text-right">
                    <li className="flex items-center gap-1 md:justify-end"><CheckCircle size={12} weight="fill" className="text-emerald-400"/> Bronze / Silver / Gold packages</li>
                    <li className="flex items-center gap-1 md:justify-end"><CheckCircle size={12} weight="fill" className="text-emerald-400"/> AI-designed, human-supervised</li>
                    <li className="flex items-center gap-1 md:justify-end"><CheckCircle size={12} weight="fill" className="text-emerald-400"/> Printed & delivered across India</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <SupportContactInline variant="hero"/>
              </div>
            </div>
          </motion.div>
        )}
      </section>
    </div>
  );
}
